/**
 * SUB-1.98 · F-82 · D83 · which account's videos the FAQ and training pages show.
 *
 * ⚠️ THE BUG THIS REPLACES. `videoService.getVideosBySection` resolved scope by
 * reading `users.tenant_id`, the caller's HOME account, and applied
 *
 *     .or(`is_global.eq.true,tenant_id.eq.${homeTenantId}`)
 *
 * on top of RLS. A Superadmin who switched accounts kept seeing their home
 * account's videos, so the switcher appeared to do nothing on that page, while
 * `playlists` and `playlist_requirements` on the SAME page resolve through RLS
 * and did follow it. Measured 2026-08-17: home `73ef7154` owns 10 videos, the
 * then-active account `08058ed4` owned 0, and the page showed 10.
 *
 * D83, taken by Allan 2026-08-17: **follow the switcher.** Resolve scope with
 * `getCurrentTenantId()`, which calls the `get_current_user_tenant_id` RPC and is
 * the same helper `commentService` and `DashboardSettingsDrawer` already use.
 *
 * ⚠️ The rejected alternative was deleting the client filter and letting RLS
 * decide. That would have shown a Superadmin every account's training material in
 * one undifferentiated list (15 active videos across all accounts), which is a
 * product behaviour nobody had decided.
 *
 * ⚠️ SO THIS UNDER-SHOWS ON PURPOSE, AND THAT IS NOT A BUG. Switch into an
 * account that owns no videos and the page shows none, even for a Superadmin
 * whose RLS grant would allow more. That is the honest answer for that account.
 * Do not "fix" it by widening the filter without revisiting D83.
 */

/** What the caller should do once scope has been resolved. */
export type VideoScope =
  | { kind: 'account'; filter: string }
  | { kind: 'global-only'; filter: string };

/**
 * Build the PostgREST `.or()` filter for a video list.
 *
 * ⚠️ `tenantId` being null is a real case, not a defensive nicety: an account
 * whose session has not resolved yet, and any caller without one. Interpolating
 * null into the filter string produces `tenant_id.eq.null`, which PostgREST
 * parses as the literal string "null" rather than SQL NULL and silently matches
 * nothing while looking like a working query. Returning a global-only filter
 * makes that case explicit and testable.
 */
export function videoScopeFilter(tenantId: string | null | undefined): VideoScope {
  if (!isUuid(tenantId)) {
    return { kind: 'global-only', filter: 'is_global.eq.true' };
  }
  return { kind: 'account', filter: `is_global.eq.true,tenant_id.eq.${tenantId}` };
}

/**
 * ⚠️ The UUID test is not defensive noise, and it is not mine: `knowledgeContentService`
 * already does exactly this before the same `.or()` interpolation, with the note
 * "never trust it raw in a filter grammar". PostgREST's `.or()` takes a
 * mini-language, not a bound parameter, so a value carrying a comma or a dot
 * changes the SHAPE of the filter rather than being rejected by it. The value
 * here comes from an RPC and should always be a UUID or null, which is precisely
 * why an unexpected one should fall back to global-only rather than be pasted in.
 */
function isUuid(value: string | null | undefined): value is string {
  return (
    typeof value === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
  );
}
